import { useEffect, useMemo, useState } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, AlertTriangle } from "lucide-react";

/**
 * Dialog d'édition d'une soumission d'heures (admin uniquement).
 * Fonctionne pour `pending` (édition du payload uniquement) ET `approved`
 * (édition + réécriture des work_entries générées en cascade).
 */
const SubmissionEditDialog = ({ open, submission, onClose, onSaved }) => {
  const [entries, setEntries] = useState([]);
  const [notes, setNotes] = useState("");
  const [orders, setOrders] = useState([]);
  const [savs, setSavs] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !submission) return;
    const init = (submission.payload?.entries || []).map((e) => ({ ...e, _order_search: "" }));
    setEntries(init.length ? init : [{ date: new Date().toISOString().slice(0, 10), start_time: "07:00", end_time: "12:00", entry_type: "commande", reference_id: "", custom_label: "", comment: "", _order_search: "" }]);
    setNotes(submission.payload?.notes || "");
    (async () => {
      try {
        const [o, s, sup] = await Promise.all([
          api.getOrders({ include_hidden: false }),
          api.getSAVs?.() ?? Promise.resolve({ data: [] }),
          api.getSuppliers?.() ?? Promise.resolve({ data: [] }),
        ]);
        setOrders((o.data || []).filter((x) => x.status !== "cancelled"));
        setSavs((s.data || []).filter((x) => !["termine", "annule"].includes(x.status)));
        setSuppliers(sup.data || []);
      } catch { /* non bloquant */ }
    })();
  }, [open, submission]);

  const total = useMemo(() => {
    return entries.reduce((sum, e) => {
      if (!e.start_time || !e.end_time) return sum;
      const [sh, sm] = e.start_time.split(":").map(Number);
      const [eh, em] = e.end_time.split(":").map(Number);
      return sum + Math.max(0, ((eh * 60 + em) - (sh * 60 + sm)) / 60);
    }, 0);
  }, [entries]);

  const updateEntry = (i, patch) => {
    setEntries((arr) => arr.map((e, idx) => idx === i ? { ...e, ...patch } : e));
  };

  const removeEntry = (i) => setEntries((arr) => arr.filter((_, idx) => idx !== i));
  const addEntry = () => setEntries((arr) => [...arr, { date: arr[arr.length - 1]?.date || new Date().toISOString().slice(0, 10), start_time: "13:00", end_time: "17:00", entry_type: "commande", reference_id: "", custom_label: "", comment: "", _order_search: "" }]);

  const save = async () => {
    for (let i = 0; i < entries.length; i++) {
      const e = entries[i];
      if (!e.date || !e.start_time || !e.end_time) {
        toast.error(`Tranche ${i + 1} : date / horaires obligatoires`);
        return;
      }
      if (["commande", "sav", "fournisseur"].includes(e.entry_type) && !e.reference_id) {
        toast.error(`Tranche ${i + 1} : référence obligatoire pour le type ${e.entry_type}`);
        return;
      }
    }
    const clean = entries.map(({ _order_search, ...rest }) => ({
      ...rest,
      comment: (rest.comment || "").trim() || null,
      reference_id: rest.reference_id || null,
      custom_label: rest.custom_label || null,
    }));
    try {
      setSaving(true);
      const newPayload = {
        ...submission.payload,
        entries: clean,
        notes: notes.trim() || null,
      };
      await api.updateEmployeeSubmission(submission.id, { payload: newPayload });
      toast.success(submission.status === "approved" ? "Fiche modifiée — heures mises à jour" : "Fiche modifiée");
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(getErrorMessage(err, "Échec"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="sm:max-w-[840px] max-h-[90vh] overflow-y-auto" data-testid="submission-edit-dialog">
        <DialogHeader>
          <DialogTitle>Éditer la fiche d'heures</DialogTitle>
          <DialogDescription>
            {submission?.employee_name} —{" "}
            <Badge variant="outline">{submission?.status === "pending" ? "En attente" : "Approuvée"}</Badge>
            {submission?.status === "approved" && (
              <span className="ml-2 text-amber-700 text-xs inline-flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                La modification met aussi à jour les heures dans le planning.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {entries.map((e, i) => (
            <div key={i} className="border rounded-md p-3 space-y-2 bg-slate-50/60">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 items-end">
                <div>
                  <Label className="text-xs">Date</Label>
                  <Input type="date" value={e.date} onChange={(ev) => updateEntry(i, { date: ev.target.value })} className="h-8" data-testid={`edit-sub-date-${i}`} />
                </div>
                <div>
                  <Label className="text-xs">Début</Label>
                  <Input type="time" value={e.start_time} onChange={(ev) => updateEntry(i, { start_time: ev.target.value })} className="h-8" data-testid={`edit-sub-start-${i}`} />
                </div>
                <div>
                  <Label className="text-xs">Fin</Label>
                  <Input type="time" value={e.end_time} onChange={(ev) => updateEntry(i, { end_time: ev.target.value })} className="h-8" data-testid={`edit-sub-end-${i}`} />
                </div>
                <div>
                  <Label className="text-xs">Type</Label>
                  <Select value={e.entry_type} onValueChange={(v) => updateEntry(i, { entry_type: v, reference_id: "", custom_label: "" })}>
                    <SelectTrigger className="h-8" data-testid={`edit-sub-type-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="commande">Commande</SelectItem>
                      <SelectItem value="sav">SAV</SelectItem>
                      <SelectItem value="fournisseur">Fournisseur</SelectItem>
                      <SelectItem value="atelier">Atelier</SelectItem>
                      <SelectItem value="custom">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {e.entry_type === "commande" && (() => {
                const q = (e._order_search || "").trim().toLowerCase();
                const sel = orders.find((o) => o.id === e.reference_id);
                if (sel && !q) {
                  return (
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="border-indigo-300 bg-indigo-50 text-indigo-800 truncate max-w-[400px]">{sel.reference} — {sel.client_name}</Badge>
                      <Button size="sm" variant="ghost" onClick={() => updateEntry(i, { reference_id: "", _order_search: "" })}>Changer</Button>
                    </div>
                  );
                }
                const filtered = q
                  ? orders.filter((o) => (
                      (o.reference || "").toLowerCase().includes(q)
                      || (o.client_name || "").toLowerCase().includes(q)
                      || (o.city || "").toLowerCase().includes(q)
                      || (o.order_type_name || "").toLowerCase().includes(q)
                    )).slice(0, 20)
                  : [];
                return (
                  <div className="relative">
                    <Input
                      placeholder="Tapez un client, ville, n° ou type…"
                      value={e._order_search}
                      onChange={(ev) => updateEntry(i, { _order_search: ev.target.value, reference_id: "" })}
                      className="h-8"
                      autoComplete="off"
                    />
                    {q && filtered.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto bg-white border rounded-md shadow-lg">
                        {filtered.map((o) => (
                          <button key={o.id} type="button" className="w-full text-left px-3 py-1.5 text-sm hover:bg-indigo-50 border-b last:border-b-0" onClick={() => updateEntry(i, { reference_id: o.id, _order_search: "" })}>
                            <span className="font-medium">{o.client_name}</span>
                            {o.city && <span className="text-muted-foreground"> — {o.city}</span>}
                            {o.order_type_name && <span className="text-indigo-700"> — {o.order_type_name}</span>}
                            <div className="font-mono text-[10px] text-muted-foreground">{o.reference}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {e.entry_type === "sav" && (
                <Select value={e.reference_id} onValueChange={(v) => updateEntry(i, { reference_id: v })}>
                  <SelectTrigger className="h-8"><SelectValue placeholder="— Choisir un SAV en cours —" /></SelectTrigger>
                  <SelectContent>
                    {savs.map((s) => <SelectItem key={s.id} value={s.id}>SAV {s.client_name || s.order_reference || s.id.slice(0, 8)}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}

              {e.entry_type === "fournisseur" && (
                <Select value={e.reference_id} onValueChange={(v) => updateEntry(i, { reference_id: v })}>
                  <SelectTrigger className="h-8"><SelectValue placeholder="— Choisir un fournisseur —" /></SelectTrigger>
                  <SelectContent>
                    {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name || s.company_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}

              {e.entry_type === "custom" && (
                <Input placeholder="Description libre" value={e.custom_label} onChange={(ev) => updateEntry(i, { custom_label: ev.target.value })} className="h-8" />
              )}

              <Input
                placeholder="Commentaire (optionnel, max 500 caractères)"
                value={e.comment || ""}
                onChange={(ev) => updateEntry(i, { comment: ev.target.value })}
                className="h-8 text-xs"
                maxLength={500}
                data-testid={`edit-sub-comment-${i}`}
              />

              <div className="flex justify-between items-center">
                <Button size="sm" variant="ghost" onClick={() => removeEntry(i)} className="text-red-600 h-7" disabled={entries.length === 1}>
                  <Trash2 className="h-3 w-3 mr-1" />Supprimer la tranche
                </Button>
                <Button size="sm" variant="ghost" onClick={addEntry} className="text-indigo-700 h-7">
                  <Plus className="h-3 w-3 mr-1" />Ajouter une tranche
                </Button>
              </div>
            </div>
          ))}

          <div>
            <Label className="text-xs">Notes globales de la fiche</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optionnel" data-testid="edit-sub-notes" />
          </div>

          <div className="text-right text-sm">
            Total : <span className="font-bold">{total.toFixed(2)}h</span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Annuler</Button>
          <Button onClick={save} disabled={saving} data-testid="submission-edit-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SubmissionEditDialog;
